import React, { useEffect, useRef } from 'react';
import { Modal, Text } from '@mantine/core';
import { ensureWafSdk, WAF_CAPTCHA_API_KEY } from '../../utils/wafToken';

export interface WafCaptchaModalProps {
    opened: boolean;
    /** Called when the person dismisses the puzzle without solving it. */
    onClose: () => void;
    /** Called once the puzzle is solved. The caller re-submits with a freshly minted token. */
    onSolved: () => void;
}

/**
 * Hosts the AWS WAF CAPTCHA puzzle for the two public forms.
 *
 * A person only ever sees this when WAF decides their source is abusive - a rate spike from one IP, or
 * an anonymising/VPN IP on the auth paths. The normal path is the silent `Challenge`, which produces a
 * token in the background with no UI at all (see common/utils/wafToken.ts).
 *
 * The puzzle is rendered by the SDK into a plain container element; `renderCaptcha` mints the token
 * itself on success and the SDK caches it, so the caller only has to re-submit - `getWafToken()` will
 * hand back the solved token.
 */
const WafCaptchaModal = ({ opened, onClose, onSolved }: WafCaptchaModalProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Held in refs so the render effect depends on `opened` alone. The callers pass inline arrows, and
    // re-running renderCaptcha on every parent re-render would tear down a puzzle mid-solve.
    const onCloseRef = useRef(onClose);
    const onSolvedRef = useRef(onSolved);
    onCloseRef.current = onClose;
    onSolvedRef.current = onSolved;

    useEffect(() => {
        if (!opened) return undefined;

        let cancelled = false;

        void (async () => {
            await ensureWafSdk();
            if (cancelled) return;

            const container = containerRef.current;
            if (!container || !window.AwsWafCaptcha || !WAF_CAPTCHA_API_KEY) {
                // No SDK and no key means the puzzle cannot be drawn. Closing hands control back to the
                // form, which surfaces an ordinary error rather than an empty modal.
                onCloseRef.current();
                return;
            }

            container.innerHTML = '';
            window.AwsWafCaptcha.renderCaptcha(container, {
                apiKey: WAF_CAPTCHA_API_KEY,
                dynamicWidth: true,
                onSuccess: () => {
                    if (!cancelled) onSolvedRef.current();
                },
                onError: (err) => {
                    console.error('WAF captcha error', err);
                },
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [opened]);

    return (
        <Modal opened={opened} onClose={onClose} centered title="Quick security check">
            <Text size="sm" color="dimmed" mb="md">
                Please complete the check below so we know you&apos;re not a bot. Your submission is sent
                as soon as it passes.
            </Text>
            <div ref={containerRef} />
        </Modal>
    );
};

export default WafCaptchaModal;
